import type { Answers, CoverageState } from "./types"
import type { PriceMap } from "@/lib/pricing"
import { recommend } from "./recommend"
import { totalPrice, addOnPriceKey } from "./product-mapping"
import {
  baseTierBookingProductId,
  addOnBookingProductId,
} from "./product-info-map"
import { resolveAddOnIds } from "./resolve-add-on-ids"
import { resolveCoverageState, TIER_TO_SERVICE } from "./coverage"
import { findInsurer, findPackage, type InsurersData } from "./insurers"
import { bookingUrlForCountry, membershipBookingUrl } from "@/lib/booking"
import { memberPriceChf } from "@/lib/pricing/member-prices"
import { isQuizMembershipEligible } from "./membership-variant"
import { isBookable, type QuizMarket } from "./market"

export interface MarketingQuizCompletedPayload {
  email: string
  locale: string
  /** URL country code (`ch` / `nl` / `de`). email-flows resolves the content
   *  locale from locale+country; without it, `nl` fell back to `en-CH`. */
  country: string
  /** ISO code for the money fields below. The `*_chf` field names are kept as
   *  legacy labels (renaming them breaks Swiss completions in whichever order
   *  the two repos deploy, because the API schema is `.strict()`), so this is
   *  what actually says which currency the numbers are in. */
  currency: string
  /**
   * Whether the recommendation is bookable in this market or waitlist-only.
   * A `waitlist` result has no `booking_url`, and the recommendation email has
   * to switch to its waitlist variant — "book now" for a product the market
   * can't sell is worse than sending nothing.
   */
  result_kind: "booking" | "waitlist"
  base_tier: string
  base_product_id: string
  add_ons: {
    id: string
    product_id: string | null
    /** Standard one-off price in `currency` (0 for included/free add-ons like
     *  trend_overlay). Absent when the market ships no figure for it. */
    price_chf?: number
    /** Member price in CHF; null when there's no distinct member price (free add-ons, or no member-price mapping). */
    member_price_chf: number | null
  }[]
  /** Which result layout the user saw. "membership-default" for the blood-only CH side-by-side, "control" (single recommendation card) for everyone else. */
  result_variant: "control" | "membership-default"
  /** Membership recurring fee (CHF, base blood test included). Present only when result_variant is "membership-default". */
  membership_price_chf?: number
  /** Locale-scoped membership sign-up URL. Present only when result_variant is "membership-default". */
  membership_booking_url?: string
  insurer_id?: string
  /** Human-readable insurer name (e.g. "Helsana"); omitted when no insurer was chosen. */
  insurer_name?: string
  /** Reimbursement coverage for the recommended tier with the chosen insurer. */
  coverage_state?: CoverageState
  /** Estimated reimbursement in CHF when coverage_state is "known". */
  coverage_amount_chf?: number
  /** Chosen supplementary-plan id (e.g. "atupri-basic"); omitted when no insurer. */
  insurer_package_id?: string
  /** Chosen supplementary-plan display name (e.g. "Basic"). */
  insurer_package?: string
  /**
   * Total in `currency`. ABSENT when the market ships no authoritative price
   * for the recommended package (EUR markets carry no MRI figures yet). It must
   * be omitted rather than sent as NaN: `JSON.stringify(NaN)` is `null`, the API
   * route's schema is `.strict()` with `total_price_chf: z.number()`, so a NaN
   * total 400d the whole submission and produced no event and no email at all.
   */
  total_price_chf?: number
  /** Absent on a `waitlist` result — there is nothing to link to. */
  booking_url?: string
  marketing_consent: boolean
  utm_source?: string
  gclid?: string
}

/**
 * Drop a non-finite number rather than letting it reach the wire. A missing
 * price key makes `Number(undefined)` NaN, `JSON.stringify` turns that into
 * `null`, and the API route's `.strict()` schema rejects null for a numeric
 * field — killing the event, the email and the Slack ping silently.
 */
function finiteOrUndefined(n: number): number | undefined {
  return Number.isFinite(n) ? n : undefined
}

export function buildCompletionPayload(args: {
  answers: Answers
  prices: PriceMap
  productIds: PriceMap
  insurers: InsurersData
  locale: string
  email: string
  marketingConsent: boolean
  search: string
  country: string
  market: QuizMarket
}): MarketingQuizCompletedPayload {
  const {
    answers,
    prices,
    productIds,
    insurers,
    locale,
    email,
    marketingConsent,
    search,
    country,
    market,
  } = args
  const rec = recommend(answers, market)
  const addOnIds = resolveAddOnIds(rec.addOns, answers.gender, productIds)
  const baseId = baseTierBookingProductId(rec.baseTier)
  const params = new URLSearchParams(search)

  const FREE_ADDONS = new Set(["trend_overlay"])
  const result_variant = isQuizMembershipEligible(rec.baseTier, country)
    ? "membership-default"
    : "control"

  const add_ons = rec.addOns.map((id) => {
    const key = addOnPriceKey(id, answers.gender)
    const isFree = FREE_ADDONS.has(id)
    const price_chf =
      isFree || !key ? 0 : finiteOrUndefined(Number(prices[key]))
    const member_price_chf = isFree || !key ? null : memberPriceChf(prices, key)
    return {
      id,
      product_id: addOnBookingProductId(id, answers.gender),
      price_chf,
      member_price_chf,
    }
  })

  // Insurer + reimbursement coverage — only when an insurer was actually picked.
  // Mirrors the result-page coverage band (components/quiz/result/coverage-band.tsx).
  let insurer_name: string | undefined
  let insurer_package: string | undefined
  let coverage_state: CoverageState | undefined
  let coverage_amount_chf: number | undefined
  if (
    answers.insurer_id &&
    answers.insurer_id !== "none" &&
    answers.insurer_package_id
  ) {
    insurer_name =
      findInsurer(insurers, answers.insurer_id)?.name ?? answers.insurer_id
    insurer_package =
      findPackage(insurers, answers.insurer_id, answers.insurer_package_id)
        ?.name ?? answers.insurer_package_id
    const coverageLocale = locale === "en" || locale === "fr" ? locale : "de"
    const coverage = resolveCoverageState(
      insurers,
      answers.insurer_id,
      answers.insurer_package_id,
      TIER_TO_SERVICE[rec.baseTier],
      coverageLocale
    )
    coverage_state = coverage.state
    coverage_amount_chf = coverage.amount
  }

  const result_kind: "booking" | "waitlist" = isBookable(
    market.tiers[rec.baseTier]
  )
    ? "booking"
    : "waitlist"

  return {
    email,
    locale,
    country,
    currency: market.currency,
    result_kind,
    base_tier: rec.baseTier,
    base_product_id: baseId,
    add_ons,
    result_variant,
    membership_price_chf:
      result_variant === "membership-default"
        ? Number(prices.blood)
        : undefined,
    membership_booking_url:
      result_variant === "membership-default"
        ? membershipBookingUrl(locale)
        : undefined,
    insurer_id: answers.insurer_id,
    insurer_name,
    coverage_state,
    coverage_amount_chf,
    insurer_package_id: answers.insurer_package_id,
    insurer_package,
    total_price_chf: finiteOrUndefined(
      totalPrice(prices, rec.baseTier, rec.addOns, answers.gender)
    ),
    booking_url:
      result_kind === "booking"
        ? bookingUrlForCountry(country, locale, [baseId, ...addOnIds])
        : undefined,
    marketing_consent: marketingConsent,
    utm_source: params.get("utm_source") ?? undefined,
    gclid: params.get("gclid") ?? undefined,
  }
}
